import { mdiEye, mdiPlus } from "@mdi/js";
import { Button, Tag } from "antd";
import { FailureTaskVm } from "api/generated/lumen";
import { useDispatch, useSelector } from "app/store";
import { MaterialIcon } from "components/MaterialIcon";
import BooleanDisplay from "components/ui/BooleanDisplay";
import IconButton from "components/ui/IconButton";
import useTable from "hooks/useTable";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "widgets/table/CustomTable";
import { ColumnStorageName } from "widgets/table/table-settings/ColumnStorageName";
import {
  ColumnState,
  ExtendedColumnType,
} from "widgets/table/table-settings/ExtendedColumnType";
import {
  ColumnConfigParams,
  FilterMode,
  ListRequestParams,
} from "widgets/table/useTableUtils";
import FailureTaskCreateModal from "../create/FailureTaskCreateModal";
import { Permission } from "components/Permission";
import Claims from "features/auth/claims";

const FailureTaskList: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { listParams, list } = useSelector((state) => state.failureTaskList);
  const { fetchList } = useSelector(
    (state) => state.loading.effects.failureTaskList
  );
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const fetchTableData = useCallback(
    (requestParams: ListRequestParams) =>
      dispatch.failureTaskList.fetchList(requestParams),
    [dispatch.failureTaskList]
  );

  const columnParams = useMemo<ColumnConfigParams<FailureTaskVm>[]>(
    () => [
      {
        key: "name",
        sort: true,
        defaultState: ColumnState.AlwaysVisible,
        filterMode: FilterMode.SEARCH,
        title: t("failureTask.name"),
      },
      {
        key: "description",
        sort: true,
        defaultState: ColumnState.AlwaysVisible,
        filterMode: FilterMode.SEARCH,
        title: t("failureTask.description"),
      },
      {
        key: "isActive",
        sort: true,
        defaultState: ColumnState.AlwaysVisible,
        filterMode: FilterMode.ACTIVE_INACTIVE,
        title: t("failureTask.status"),
        render: (value: boolean) => {
          return (
            <BooleanDisplay
              value={value}
              trueDisplay={<Tag color="success">{t("common.active")}</Tag>}
              falseDisplay={<Tag color="error">{t("common.inactive")}</Tag>}
            />
          );
        },
      },
    ],
    [t]
  );

  const handleNewButtonClick = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  const handleViewButtonClick = useCallback(
    (id?: string) => {
      dispatch.failureTaskCreate.setEditingItemId(id);

      setCreateModalOpen(true);
    },
    [dispatch.failureTaskCreate]
  );

  const actionColumnParams = useMemo<
    Partial<ExtendedColumnType<FailureTaskVm>> | undefined
  >(
    () => ({
      fixed: "right",
      columnOrderFixed: true,
      width: 90,
      render(item: any, record) {
        return (
          <IconButton
            path={mdiEye}
            onClick={() => handleViewButtonClick(record.id)}
            title={t("common.details")}
          />
        );
      },
      title: t("common.table.actions"),
    }),
    [handleViewButtonClick, t]
  );

  const customTableProps = useTable({
    fetchTableData,
    columnParams,
    actionColumnParams,
    listParams,
    list,
    columnStorageName: ColumnStorageName.FAILURE_TASK,
    headerOptions: (
      <Permission
        acceptableClaims={[
          Claims.Failure.Write,
          Claims.Failure.WriteCallCenter,
        ]}
      >
        <Button
          icon={<MaterialIcon path={mdiPlus} />}
          type="primary"
          onClick={handleNewButtonClick}
        >
          {t("common.new")}
        </Button>
      </Permission>
    ),
    resetListParams: dispatch.failureTaskList.resetListParams,
    loading: fetchList.loading,
    resetStore: dispatch.failureTaskList.reset,
  });

  return (
    <>
      <CustomTable<FailureTaskVm>
        {...customTableProps}
        scrollX={800}
        selectable={false}
      />

      <FailureTaskCreateModal
        visible={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        fetchDataParams={listParams}
      />
    </>
  );
};

export default FailureTaskList;
